import { Sprite, Texture } from 'pixi.js';
import { WEAPON_MAX_LEVEL } from '../../../constants';
import type { GameWorld } from '../../../world/GameWorld';
import type { LayerStackSprite } from '../../layerstack/LayerStackSprite';
import { PLAYER_SLUG } from '../assets/registry';
import type { StackRegistry } from '../assets/StackRegistry';
import { placeSprite } from '../scale';
import type { EntityFamily, FamilyDeps, FrameContext } from './types';

/** Rendered frames the firing pose outlives the shot that caused it. */
const FIRING_FRAMES = 6;
const STATE_DAMAGED = 'damaged';
const STATE_FIRING = 'firing';
const STATE_CRUISE = 'cruise';

/** Manifest anchor the muzzle flash is pinned to; shared name with FxDirector's shot origin. */
const NOSE_ANCHOR = 'noseEmitter';

/** Rendered frames the muzzle flash is held for after a shot fires. */
const MUZZLE_FLASH_FRAMES = 4;
const MUZZLE_FLASH_COLOR = 0x9dfff2;
/** Indexed by weaponLevel (0..WEAPON_MAX_LEVEL): subtle at L0-1, pronounced at L4. */
const MUZZLE_FLASH_SIZE_PX = new Float32Array([3, 3, 4, 5, 6]);
const MUZZLE_FLASH_ALPHA = new Float32Array([0.4, 0.45, 0.6, 0.75, 0.95]);

/** Lv5 aura: sinusoidal glow behind the ship, shown only at WEAPON_MAX_LEVEL. */
const AURA_COLOR = 0x70fff2;
const AURA_PERIOD_TICKS = 40;
const AURA_ALPHA_MIN = 0.22;
const AURA_ALPHA_MAX = 0.55;
const AURA_SIZE_SCALE = 1.7;
const TAU = Math.PI * 2;

/**
 * オーラのフォールオフ素材。`Texture.WHITE` を加算合成すると、自機の周りに輪郭の
 * ある矩形がそのまま出る（Lv5 到達が遅く常時観測できなかったため見落とされていた）。
 * 中心から外へ落ちる円形の階調を 1 枚だけ焼き、以後は全インスタンスで共有する。
 * 階調はなめらかな勾配ではなく離散ステップ + nearest 拡大にする — PixelBurst と
 * 同じ理由で、連続フェードはドット絵に対してブラーに見えるため。
 */
const AURA_TEXTURE_PX = 32;
/** 中心からの正規化半径の上限と、そこに割り当てるアルファ。上限外は完全透明。 */
interface AuraFalloffStep { readonly limit: number; readonly alpha: number }
const AURA_FALLOFF_STEPS: readonly AuraFalloffStep[] = Object.freeze([
  Object.freeze({ limit: 0.34, alpha: 1 }),
  Object.freeze({ limit: 0.56, alpha: 0.62 }),
  Object.freeze({ limit: 0.78, alpha: 0.3 }),
  Object.freeze({ limit: 1, alpha: 0.12 }),
]);
let auraFalloffTexture: Texture | null = null;

function getAuraFalloffTexture(): Texture {
  if (auraFalloffTexture) return auraFalloffTexture;
  const canvas = document.createElement('canvas');
  canvas.width = AURA_TEXTURE_PX;
  canvas.height = AURA_TEXTURE_PX;
  const ctx = canvas.getContext('2d');
  // headless/CI の 2D コンテキスト差で描けないときは従来の矩形へ落とす（描画は続く）。
  if (!ctx) return Texture.WHITE;
  const center = AURA_TEXTURE_PX / 2;
  const image = ctx.createImageData(AURA_TEXTURE_PX, AURA_TEXTURE_PX);
  for (let y = 0; y < AURA_TEXTURE_PX; y += 1) {
    for (let x = 0; x < AURA_TEXTURE_PX; x += 1) {
      const dx = (x + 0.5 - center) / center;
      const dy = (y + 0.5 - center) / center;
      const radius = Math.sqrt(dx * dx + dy * dy);
      const step = AURA_FALLOFF_STEPS.find((candidate) => radius <= candidate.limit);
      const alpha = step ? step.alpha : 0;
      const offset = (y * AURA_TEXTURE_PX + x) * 4;
      image.data[offset] = 0xff;
      image.data[offset + 1] = 0xff;
      image.data[offset + 2] = 0xff;
      image.data[offset + 3] = Math.round(alpha * 0xff);
    }
  }
  ctx.putImageData(image, 0, 0);
  const texture = Texture.from(canvas);
  texture.source.scaleMode = 'nearest';
  auraFalloffTexture = texture;
  return texture;
}

/**
 * Renderer-local ring burst on a weapon level-up. No sim/port involvement and
 * no FxDirector dependency (FxDirector is not yet wired into the live game
 * loop) — a fixed pool of pre-created debris sprites, moved and faded by
 * fixed per-sync-call steps, the same frame-counted discipline as this
 * family's own `firingFrames` and BossFamily's `hitFrames`/`enterFrames`.
 */
const LEVEL_UP_RING_COUNT = 10;
const LEVEL_UP_RING_LIFE_FRAMES = 18;
const LEVEL_UP_RING_SPEED_PX = 3.4;
const LEVEL_UP_RING_SIZE_PX = 2;
const LEVEL_UP_RING_COLOR = 0xff5fa8;
/** Rotates each burst so repeated level-ups never line up, same trick as PixelBurst. */
const GOLDEN_ANGLE_RAD = Math.PI * (3 - Math.sqrt(5));

/** The VEIL SKIMMER: one sprite, mirrored, posed from hull and fire state. */
export class PlayerFamily implements EntityFamily {
  private stacks: StackRegistry | null = null;
  private sprite: LayerStackSprite | null = null;
  private firingFrames = 0;
  private fireCooldown = 0;

  private muzzleFlash: Sprite | null = null;
  private muzzleFlashFrames = 0;
  private muzzleFlashSize = 0;

  private aura: Sprite | null = null;

  /** -1 means "not yet observed": the first sync just records, never bursts. */
  private prevWeaponLevel = -1;
  private readonly ringSprites: Sprite[] = [];
  private readonly ringX = new Float32Array(LEVEL_UP_RING_COUNT);
  private readonly ringY = new Float32Array(LEVEL_UP_RING_COUNT);
  private readonly ringVx = new Float32Array(LEVEL_UP_RING_COUNT);
  private readonly ringVy = new Float32Array(LEVEL_UP_RING_COUNT);
  private readonly ringLife = new Int16Array(LEVEL_UP_RING_COUNT);
  private ringBurstIndex = 0;

  init(deps: FamilyDeps): void {
    this.stacks = deps.stacks;

    // Paint order within the family layer: aura behind, ship above it, then
    // the level-up rings and muzzle flash in front of the ship — children
    // added in that order is the z-order (same "call order is paint order"
    // rule the composition root documents).
    const aura = new Sprite(getAuraFalloffTexture());
    aura.roundPixels = true;
    aura.visible = false;
    aura.tint = AURA_COLOR;
    aura.blendMode = 'add';
    deps.layer.addChild(aura);
    this.aura = aura;

    const sprite = deps.stacks.acquire(PLAYER_SLUG, deps.layer);
    sprite.setMirror(true);
    this.sprite = sprite;

    for (let i = 0; i < LEVEL_UP_RING_COUNT; i += 1) {
      const debris = new Sprite(Texture.WHITE);
      debris.roundPixels = true;
      debris.visible = false;
      debris.tint = LEVEL_UP_RING_COLOR;
      deps.layer.addChild(debris);
      this.ringSprites.push(debris);
    }

    const flash = new Sprite(Texture.WHITE);
    flash.roundPixels = true;
    flash.visible = false;
    flash.tint = MUZZLE_FLASH_COLOR;
    deps.layer.addChild(flash);
    this.muzzleFlash = flash;
  }

  sync(world: GameWorld, _frame: FrameContext): void {
    const sprite = this.sprite;
    const stacks = this.stacks;
    if (!sprite || !stacks) return;
    const player = world.player;

    // Independent of the ship's alive state: a burst already in flight keeps
    // animating even across a death (matches PixelBurst's own detachment from
    // whatever triggered it).
    this.updateLevelUpRing();

    // A shot resets fireCooldown upwards; the tail keeps the pose readable
    // after a fast core beam's cooldown has already run out.
    const firedThisFrame = player.fireCooldown > this.fireCooldown;
    if (firedThisFrame) this.firingFrames = FIRING_FRAMES;
    else if (this.firingFrames > 0) this.firingFrames -= 1;
    this.fireCooldown = player.fireCooldown;

    sprite.visible = player.alive;
    if (!player.alive) {
      this.hideMuzzleFlash();
      this.hideAura();
      return;
    }
    const firing = player.fireCooldown > 0 || this.firingFrames > 0;
    const state = player.invulnerabilityTicks > 0 ? STATE_DAMAGED : firing ? STATE_FIRING : STATE_CRUISE;
    stacks.applyState(PLAYER_SLUG, sprite, state);
    placeSprite(sprite, player.x, player.y);

    this.syncMuzzleFlash(sprite, player.weaponLevel, firedThisFrame);
    this.syncAura(sprite, world, player.weaponLevel);
    this.syncWeaponLevelBurst(sprite, player.weaponLevel);
  }

  /**
   * Dropped rather than carried across a scene change: renderer-local tails
   * (a level-up burst mid-flight, the muzzle flash pose) belong to the scene
   * that just ended, not the one starting — same discipline as
   * BossFamily.onSceneReset.
   */
  onSceneReset(): void {
    this.prevWeaponLevel = -1;
    this.ringLife.fill(0);
    this.muzzleFlashFrames = 0;
  }

  dispose(): void {
    this.sprite = null;
    this.stacks = null;
    this.muzzleFlash = null;
    this.aura = null;
    this.ringSprites.length = 0;
    this.prevWeaponLevel = -1;
    this.ringLife.fill(0);
    this.muzzleFlashFrames = 0;
  }

  private syncMuzzleFlash(sprite: LayerStackSprite, weaponLevel: number, firedThisFrame: boolean): void {
    const flash = this.muzzleFlash;
    if (!flash) return;
    if (firedThisFrame) {
      this.muzzleFlashFrames = MUZZLE_FLASH_FRAMES;
      const level = Math.max(0, Math.min(MUZZLE_FLASH_SIZE_PX.length - 1, weaponLevel));
      this.muzzleFlashSize = MUZZLE_FLASH_SIZE_PX[level]!;
      flash.alpha = MUZZLE_FLASH_ALPHA[level]!;
      flash.setSize(this.muzzleFlashSize, this.muzzleFlashSize);
    } else if (this.muzzleFlashFrames > 0) {
      this.muzzleFlashFrames -= 1;
    }
    if (this.muzzleFlashFrames > 0) {
      const hasNose = NOSE_ANCHOR in sprite.manifest.anchors;
      const nose = hasNose ? sprite.getAnchor(NOSE_ANCHOR) : null;
      // Renderer-local: actors sit in flat renderer space, so local anchor +
      // sprite position — the same convention as FxDirector.fireProjectileOnly.
      // Recomputed every sync (not just the firing frame) so the flash tracks
      // the ship's nose while the pose animation moves it.
      const noseX = nose ? sprite.x + nose.localX : sprite.x + sprite.targetWidth;
      const noseY = nose ? sprite.y + nose.localY : sprite.y + sprite.targetHeight / 2;
      const size = this.muzzleFlashSize;
      flash.position.set(Math.round(noseX - size / 2), Math.round(noseY - size / 2));
    }
    flash.visible = this.muzzleFlashFrames > 0;
  }

  private hideMuzzleFlash(): void {
    this.muzzleFlashFrames = 0;
    if (this.muzzleFlash) this.muzzleFlash.visible = false;
  }

  private syncAura(sprite: LayerStackSprite, world: GameWorld, weaponLevel: number): void {
    const aura = this.aura;
    if (!aura) return;
    if (weaponLevel < WEAPON_MAX_LEVEL) {
      aura.visible = false;
      return;
    }
    const size = Math.round(Math.max(sprite.targetWidth, sprite.targetHeight) * AURA_SIZE_SCALE);
    const phase = (world.simTick % AURA_PERIOD_TICKS) / AURA_PERIOD_TICKS;
    aura.visible = true;
    aura.alpha = AURA_ALPHA_MIN + (AURA_ALPHA_MAX - AURA_ALPHA_MIN) * (0.5 + 0.5 * Math.sin(phase * TAU));
    aura.setSize(size, size);
    aura.position.set(
      Math.round(sprite.x + sprite.targetWidth / 2 - size / 2),
      Math.round(sprite.y + sprite.targetHeight / 2 - size / 2),
    );
  }

  private hideAura(): void {
    if (this.aura) this.aura.visible = false;
  }

  /** Fires only on an increase; a death-penalty decrease gets no fx. */
  private syncWeaponLevelBurst(sprite: LayerStackSprite, weaponLevel: number): void {
    if (this.prevWeaponLevel < 0) {
      this.prevWeaponLevel = weaponLevel;
      return;
    }
    if (weaponLevel > this.prevWeaponLevel) {
      this.triggerLevelUpBurst(sprite.x + sprite.targetWidth / 2, sprite.y + sprite.targetHeight / 2);
    }
    this.prevWeaponLevel = weaponLevel;
  }

  private triggerLevelUpBurst(centerX: number, centerY: number): void {
    const rotation = this.ringBurstIndex * GOLDEN_ANGLE_RAD;
    this.ringBurstIndex += 1;
    for (let i = 0; i < LEVEL_UP_RING_COUNT; i += 1) {
      const angle = rotation + (i / LEVEL_UP_RING_COUNT) * TAU;
      this.ringX[i] = centerX;
      this.ringY[i] = centerY;
      this.ringVx[i] = Math.cos(angle) * LEVEL_UP_RING_SPEED_PX;
      this.ringVy[i] = Math.sin(angle) * LEVEL_UP_RING_SPEED_PX;
      this.ringLife[i] = LEVEL_UP_RING_LIFE_FRAMES;
    }
  }

  private updateLevelUpRing(): void {
    for (let i = 0; i < LEVEL_UP_RING_COUNT; i += 1) {
      const life = this.ringLife[i]!;
      const debris = this.ringSprites[i]!;
      if (life <= 0) {
        if (debris.visible) debris.visible = false;
        continue;
      }
      this.ringX[i] += this.ringVx[i]!;
      this.ringY[i] += this.ringVy[i]!;
      this.ringLife[i] = life - 1;
      debris.visible = true;
      debris.alpha = life / LEVEL_UP_RING_LIFE_FRAMES;
      debris.setSize(LEVEL_UP_RING_SIZE_PX, LEVEL_UP_RING_SIZE_PX);
      debris.position.set(
        Math.round(this.ringX[i]! - LEVEL_UP_RING_SIZE_PX / 2),
        Math.round(this.ringY[i]! - LEVEL_UP_RING_SIZE_PX / 2),
      );
    }
  }
}
